import { Check } from 'lucide-react';

import { cn } from '../../lib/cn.js';

export interface EtapaDaTrilha {
  readonly id: string;
  readonly rotulo: string;
  /** O que a etapa já tem — o tipo escolhido, o título, quantos arquivos —, embaixo do rótulo. */
  readonly resumo?: string | undefined;
  /** Preenchida e válida: o quadrado leva o visto. */
  readonly feita?: boolean | undefined;
  /** Dá para ir até ela agora, clicando na trilha. */
  readonly alcancavel?: boolean | undefined;
  /** Tem campo a corrigir: ganha o ponto vermelho. */
  readonly comErro?: boolean | undefined;
}

/**
 * A trilha de um assistente em etapas (design.md §8, Assistente em etapas).
 *
 * As etapas numeradas numa coluna, ligadas por um fio: a atual num quadrado
 * laranja, as já preenchidas em tinta com o visto, as que vêm pela frente só
 * com o número num quadrado vazado. Embaixo do rótulo fica o que a etapa já
 * tem, e a trilha vira o resumo do que foi preenchido até ali. Clica-se numa
 * etapa já alcançada para voltar a ela.
 *
 * No celular a coluna vira uma barra de segmentos no topo, com a etapa atual
 * escrita embaixo. `data-trilha` tira os botões daqui do foco inicial do modal:
 * a trilha é navegação, e o foco vai para o primeiro campo da etapa.
 */
export function TrilhaDeEtapas({
  rotulo,
  etapas,
  atual,
  onIr,
}: {
  rotulo: string;
  etapas: readonly EtapaDaTrilha[];
  /** Índice da etapa à vista. */
  atual: number;
  onIr: (indice: number) => void;
}) {
  return (
    <nav aria-label={rotulo} data-trilha>
      <ol className="hidden flex-col sm:flex">
        {etapas.map((etapa, indice) => {
          const ativa = indice === atual;
          const feita = !ativa && Boolean(etapa.feita);
          const clicavel = !ativa && Boolean(etapa.alcancavel);

          return (
            <li key={etapa.id} className="relative pb-7 last:pb-0">
              {/* O fio até a etapa seguinte: em tinta depois de vencida. */}
              {indice < etapas.length - 1 ? (
                <span
                  aria-hidden
                  className={cn(
                    'absolute top-8 bottom-1 left-[11.5px] w-px transition-colors duration-300',
                    feita ? 'bg-ink-3' : 'bg-line-2',
                  )}
                />
              ) : null}

              <button
                type="button"
                disabled={!clicavel}
                aria-current={ativa ? 'step' : undefined}
                onClick={() => onIr(indice)}
                className="group flex w-full items-start gap-3 text-left disabled:cursor-default"
              >
                <span
                  className={cn(
                    'tabular relative z-10 grid size-6 shrink-0 place-items-center text-[12px] font-semibold transition-colors duration-200',
                    ativa
                      ? 'bg-primary text-on-primary'
                      : feita
                        ? 'bg-ink text-on-ink'
                        : 'border border-line-2 bg-surface text-ink-3',
                    clicavel && !feita && 'group-hover:border-ink-3 group-hover:text-ink',
                  )}
                >
                  {feita ? <Check size={13} strokeWidth={2.5} aria-hidden /> : indice + 1}
                </span>

                <span className="min-w-0 flex-1 pt-[3px]">
                  <span
                    className={cn(
                      'block text-[13px] leading-[18px] transition-colors',
                      ativa
                        ? 'font-semibold text-ink'
                        : feita
                          ? 'font-medium text-ink'
                          : 'text-ink-3',
                      clicavel && 'group-hover:text-primary-ink',
                    )}
                  >
                    {etapa.rotulo}
                  </span>
                  {etapa.resumo ? (
                    <span className="mt-0.5 block truncate text-[12px] text-ink-3">
                      {etapa.resumo}
                    </span>
                  ) : null}
                </span>

                {etapa.comErro ? (
                  <span
                    aria-label="Esta etapa tem campos a corrigir"
                    className="mt-2 size-1.5 shrink-0 rounded-full bg-danger"
                  />
                ) : null}
              </button>
            </li>
          );
        })}
      </ol>

      <div className="sm:hidden">
        <div className="flex gap-1" aria-hidden>
          {etapas.map((etapa, indice) => (
            <span
              key={etapa.id}
              className={cn(
                'h-1 flex-1 transition-colors duration-300',
                indice === atual ? 'bg-primary' : indice < atual ? 'bg-ink' : 'bg-line-2',
              )}
            />
          ))}
        </div>
        <p className="mt-2 text-[12px] text-ink-3">
          Etapa {atual + 1} de {etapas.length} ·{' '}
          <span className="font-medium text-ink">{etapas[atual]?.rotulo}</span>
        </p>
      </div>
    </nav>
  );
}
